/* global JWeb */ // eslint-disable-line
import { jWebReady, ServiceInstanceEvent } from '@jarvis/jweb-core';
import pubSub from './pubSub';
import { getUserAuthToken } from './jweb';
import {
  PRINTER_SELECTED_EVENT,
  PRINTER_PUBLISHER_ID,
  AUTH_CHANGE_EVENT,
  AUTH_PUBLISHER_ID
} from './constants';

/**
 * Adds an event listener for any login changes
 * LoginChange Event Payload {
 *      account: {
 *          accountId: "[Account ID]",
 *          emailAddress: "[Email Address]",
 *          familyName: "[Family/Last Name]"
 *          givenName: "[Given/First Name]"
 *      },
 *      isLoggedIn: "[true | false]"
 * }
 *
 * @param {*} callback Callback with the JWT Value or empty string from the auth information
 * @listens LoginChange Event
 * @returns {Function} Remove Listener Function
 */
export const addLoginChangedListener = async callback => {
  try {
    const jweb = await jWebReady;
    if (jweb && jweb.isNative) {
      console.log('LoginChangedListener');
      const { EventService } = jweb.Plugins;

      const subscriber = await EventService.createSubscriber();
      if (typeof subscriber.errorType === 'undefined') {
        let subscriptionHandle = await subscriber.subscribe(
          {
            eventName: AUTH_CHANGE_EVENT,
            publisherId: AUTH_PUBLISHER_ID
          },
          async ({ eventData }) => {
            console.log(`Received login change event: ${JSON.stringify(eventData)}`);
            let jwt = '';
            if (eventData?.currentAccount?.accountId) {
              jwt = await getUserAuthToken();
            }
            callback(jwt);
          }
        );
        if (typeof subscriptionHandle.errorType === 'undefined') {
          subscriptionHandle.unsubscribe;
        }
      }
    }
  } catch (error) {
    console.error(`ADD LOGIN CHANGED LISTENER ERROR: ${error}`);
  }
};

/**
 * Adds an event listener for any printer selection changes
 * Payload {
 *      PrinterUUID: "[Printer UUID]"
 *      PrinterProductNumber: "[Product Number]"
 * }
 * @param {*} callback Callback with the value of the printer UUID in JSON
 * @listens PrinterSelected Event
 * @returns {Function} Remove Listener Function
 */
export const addPrinterSelectedListener = async callback => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { EventService } = jweb.Plugins;
      const subscriber = await EventService.createSubscriber();
      if (typeof subscriber.errorType !== 'undefined') {
        return;
      }
      let subscriptionHandle = await subscriber.subscribe(
        {
          eventName: PRINTER_SELECTED_EVENT
        },
        async ({ eventData }) => {
          console.log(`Received printer change event: ${JSON.stringify(eventData)}`);
          callback(eventData);
        }
      );
      if (typeof subscriptionHandle.errorType !== 'undefined') {
        return;
      }
      return subscriptionHandle.unsubscribe;
    }
  } catch (error) {
    console.error(`ADD PRINTER SELECTED LISTENER ERROR: ${error}`);
  }
};

/**
 * Adds an event listener for any instances the CEC is re-diplayed.
 * Payload {
 *      [empty payload]
 * }
 * @param {*} callback Callback with no value in payload
 * @listens CECShown Event
 * @returns {Function} Remove Listener Function
 */
export const addCECShownListener = async callback => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { EventService } = jweb.Plugins;
      const subscriber = await EventService.createSubscriber();
      if (typeof subscriber.errorType !== 'undefined') {
        console.error('Subscriber creation error', subscriber.errorType);
        return;
      }

      let subscriptionHandle = await subscriber.subscribe(
        {
          eventName: 'CECShown'
        },
        () => {
          console.log('CECShown: {}');
          callback();
        }
      );

      if (typeof subscriptionHandle.errorType !== 'undefined') {
        console.error('Failed to subscribe:', subscriptionHandle.errorType);
        return;
      }

      return subscriptionHandle.unsubscribe;
    }
  } catch (error) {
    console.error(`ADD CEC SHOWN LISTENER ERROR: ${error}`);
  }
};

/**
 * Adds an event listener for any Unload Event
 * Payload {
 *      [empty payload]
 * }
 * @param {*} callback Callback with the value of the Unload Data in JSON
 * @listens Unload Event
 * @returns {Function} Remove Listener Function
 */
export const addUnloadCustomListener = async callback => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { EventService } = jweb.Plugins;
      const subscriber = await EventService.createSubscriber();

      if (typeof subscriber.errorType !== 'undefined') {
        console.error('Subscriber creation error:', subscriber.errorType);
        return;
      }

      let subscriptionHandle = await subscriber.subscribe(
        {
          eventName: 'Unload'
        },
        () => {
          console.log('Unload: {}');
          callback();
        }
      );

      if (typeof subscriptionHandle.errorType !== 'undefined') {
        console.error('Failed to subscribe:', subscriptionHandle.errorType);
        return;
      }

      return subscriptionHandle.unsubscribe;
    }

    return () => window.removeEventListener('Unload', callback);
  } catch (error) {
    console.error(`ADD UNLOAD CUSTOM LISTENER ERROR: ${error}`);
  }
};

/**
 * Registers an event listener for any Service Instance State Changes

 * @param {*} publisherId Id of the service instance
 * @listens serviceInstanceStateChanged Event
 * @returns {Function} Remove Listener Function
 */
export const registerServiceInstanceClosedListener = async publisherId => {
  try {
    const jweb = await jWebReady;

    if (jweb && jweb.isNative) {
      const { EventService } = jweb.Plugins;
      const subscriber = await EventService.createSubscriber();
      if (typeof subscriber.errorType !== 'undefined') {
        return;
      }

      const serviceInstanceStateChangeListener = await subscriber.subscribe(
        {
          eventName: ServiceInstanceEvent.jarvisEventServiceInstanceClosed,
          publisherId
        },
        ({ eventData: serviceInstance }) => {
          console.log(
            `serviceInstanceStateChanged: ${JSON.stringify(
              { state: serviceInstance.state, serviceId: serviceInstance.serviceId },
              null,
              2
            )}`
          );
          pubSub.publish(ServiceInstanceEvent.jarvisEventServiceInstanceClosed, serviceInstance);
        }
      );
      pubSub.runOnUnsubscribe(
        ServiceInstanceEvent.jarvisEventServiceInstanceClosed,
        serviceInstanceStateChangeListener.unsubscribe
      );
    }
  } catch (error) {
    console.error(`REGISTER SERVICE INSTANCE STATE CHANGE LISTENER ERROR: ${error}`);
  }
};

/**
 * Adds an event listener for any Service Instance State Changes
 * Payload {
 *      serviceId: "[Service ID]",
 *      state: "[State of Service]"
 * }
 * @param {*} callback Callback with the value of the Service State Data in JSON
 * @listens serviceInstanceStateChanged Event
 * @returns {Function} Remove Listener Function
 */
export const addServiceInstanceClosedListener = callback => {
  pubSub.subscribe(ServiceInstanceEvent.jarvisEventServiceInstanceClosed, callback);
  return () => pubSub.unsubscribe(ServiceInstanceEvent.jarvisEventServiceInstanceClosed);
};
